/**
 * Dynamic Route Configuration
 *
 * Generates Refine resources and React Router routes from module manifests.
 * Modules with navigation.hidden = true are excluded from both navigation and routes.
 */

import React from "react";
import { Route } from "react-router-dom";
import { Result, Button } from "antd";
import { useNavigate } from "react-router-dom";
import type { ResourceProps } from "@refinedev/core";
import { getAllModules } from "./registry";
import { getIconComponent } from "./icons";

/**
 * Placeholder component for modules without dedicated pages yet.
 * Shows a "coming soon" message with link back to dashboard.
 */
const PlaceholderPage: React.FC<{ moduleName: string }> = ({ moduleName }) => {
  const navigate = useNavigate();
  return (
    <Result
      status="info"
      title={`${moduleName} Module`}
      subTitle="This module is registered but pages are not yet implemented. Create pages in frontend/src/pages/ to enable full functionality."
      extra={
        <Button type="primary" onClick={() => navigate("/")}>
          Back to Dashboard
        </Button>
      }
    />
  );
};

// Lazy load page components to avoid circular dependencies
// and enable code splitting
const pageComponents: Record<
  string,
  {
    List: React.LazyExoticComponent<React.FC>;
    Show: React.LazyExoticComponent<React.FC>;
    Create: React.LazyExoticComponent<React.FC>;
    Edit: React.LazyExoticComponent<React.FC>;
  }
> = {
  accounts: {
    List: React.lazy(() =>
      import("@/pages/accounts").then((m) => ({ default: m.AccountList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/accounts").then((m) => ({ default: m.AccountShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/accounts").then((m) => ({ default: m.AccountCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/accounts").then((m) => ({ default: m.AccountEdit }))
    ),
  },
  contacts: {
    List: React.lazy(() =>
      import("@/pages/contacts").then((m) => ({ default: m.ContactList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/contacts").then((m) => ({ default: m.ContactShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/contacts").then((m) => ({ default: m.ContactCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/contacts").then((m) => ({ default: m.ContactEdit }))
    ),
  },
  events: {
    List: React.lazy(() =>
      import("@/pages/events").then((m) => ({ default: m.EventList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/events").then((m) => ({ default: m.EventShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/events").then((m) => ({ default: m.EventCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/events").then((m) => ({ default: m.EventEdit }))
    ),
  },
  documents: {
    List: React.lazy(() =>
      import("@/pages/documents").then((m) => ({ default: m.DocumentList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/documents").then((m) => ({ default: m.DocumentShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/documents").then((m) => ({ default: m.DocumentCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/documents").then((m) => ({ default: m.DocumentEdit }))
    ),
  },
  // GDPR modules
  "anti-money-laundering": {
    List: React.lazy(() =>
      import("@/pages/anti-money-laundering").then((m) => ({ default: m.AMLList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/anti-money-laundering").then((m) => ({ default: m.AMLShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/anti-money-laundering").then((m) => ({ default: m.AMLCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/anti-money-laundering").then((m) => ({ default: m.AMLEdit }))
    ),
  },
  "processing-activities": {
    List: React.lazy(() =>
      import("@/pages/processing-activities").then((m) => ({ default: m.ProcessingActivityList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/processing-activities").then((m) => ({ default: m.ProcessingActivityShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/processing-activities").then((m) => ({ default: m.ProcessingActivityCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/processing-activities").then((m) => ({ default: m.ProcessingActivityEdit }))
    ),
  },
  policies: {
    List: React.lazy(() =>
      import("@/pages/policies").then((m) => ({ default: m.PolicyList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/policies").then((m) => ({ default: m.PolicyShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/policies").then((m) => ({ default: m.PolicyCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/policies").then((m) => ({ default: m.PolicyEdit }))
    ),
  },
  threats: {
    List: React.lazy(() =>
      import("@/pages/threats").then((m) => ({ default: m.ThreatList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/threats").then((m) => ({ default: m.ThreatShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/threats").then((m) => ({ default: m.ThreatCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/threats").then((m) => ({ default: m.ThreatEdit }))
    ),
  },
  "threats-measures": {
    List: React.lazy(() =>
      import("@/pages/threats-measures").then((m) => ({ default: m.ThreatsMeasureList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/threats-measures").then((m) => ({ default: m.ThreatsMeasureShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/threats-measures").then((m) => ({ default: m.ThreatsMeasureCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/threats-measures").then((m) => ({ default: m.ThreatsMeasureEdit }))
    ),
  },
  audit: {
    List: React.lazy(() =>
      import("@/pages/audit").then((m) => ({ default: m.AuditList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/audit").then((m) => ({ default: m.AuditShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/audit").then((m) => ({ default: m.AuditCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/audit").then((m) => ({ default: m.AuditEdit }))
    ),
  },
  acceptance: {
    List: React.lazy(() =>
      import("@/pages/acceptance").then((m) => ({ default: m.AcceptanceList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/acceptance").then((m) => ({ default: m.AcceptanceShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/acceptance").then((m) => ({ default: m.AcceptanceCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/acceptance").then((m) => ({ default: m.AcceptanceEdit }))
    ),
  },
  "data-flow": {
    List: React.lazy(() =>
      import("@/pages/data-flow").then((m) => ({ default: m.DataFlowList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/data-flow").then((m) => ({ default: m.DataFlowShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/data-flow").then((m) => ({ default: m.DataFlowCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/data-flow").then((m) => ({ default: m.DataFlowEdit }))
    ),
  },
  content: {
    List: React.lazy(() =>
      import("@/pages/content").then((m) => ({ default: m.ContentList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/content").then((m) => ({ default: m.ContentShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/content").then((m) => ({ default: m.ContentCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/content").then((m) => ({ default: m.ContentEdit }))
    ),
  },
  projects: {
    List: React.lazy(() =>
      import("@/pages/projects").then((m) => ({ default: m.ProjectList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/projects").then((m) => ({ default: m.ProjectShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/projects").then((m) => ({ default: m.ProjectCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/projects").then((m) => ({ default: m.ProjectEdit }))
    ),
  },
  "project-tasks": {
    List: React.lazy(() =>
      import("@/pages/project-tasks").then((m) => ({ default: m.ProjectTaskList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/project-tasks").then((m) => ({ default: m.ProjectTaskShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/project-tasks").then((m) => ({ default: m.ProjectTaskCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/project-tasks").then((m) => ({ default: m.ProjectTaskEdit }))
    ),
  },
  // Multi-framework concepts (Phase 3)
  assets: {
    List: React.lazy(() =>
      import("@/pages/assets").then((m) => ({ default: m.AssetList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/assets").then((m) => ({ default: m.AssetShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/assets").then((m) => ({ default: m.AssetCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/assets").then((m) => ({ default: m.AssetEdit }))
    ),
  },
  suppliers: {
    List: React.lazy(() =>
      import("@/pages/suppliers").then((m) => ({ default: m.SupplierList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/suppliers").then((m) => ({ default: m.SupplierShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/suppliers").then((m) => ({ default: m.SupplierCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/suppliers").then((m) => ({ default: m.SupplierEdit }))
    ),
  },
  incidents: {
    List: React.lazy(() =>
      import("@/pages/incidents").then((m) => ({ default: m.IncidentList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/incidents").then((m) => ({ default: m.IncidentShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/incidents").then((m) => ({ default: m.IncidentCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/incidents").then((m) => ({ default: m.IncidentEdit }))
    ),
  },
  // Workplace safety
  "worker-groups": {
    List: React.lazy(() =>
      import("@/pages/worker-groups").then((m) => ({ default: m.WorkerGroupList }))
    ),
    Show: React.lazy(() =>
      import("@/pages/worker-groups").then((m) => ({ default: m.WorkerGroupShow }))
    ),
    Create: React.lazy(() =>
      import("@/pages/worker-groups").then((m) => ({ default: m.WorkerGroupCreate }))
    ),
    Edit: React.lazy(() =>
      import("@/pages/worker-groups").then((m) => ({ default: m.WorkerGroupEdit }))
    ),
  },
};

/**
 * Get visible modules (not hidden) for navigation.
 */
export function getVisibleModules() {
  return getAllModules().filter((m) => !m.navigation.hidden);
}

/**
 * Generate Refine resource definitions from ALL modules (including hidden).
 * Hidden modules need resources for routing to work, just not in navigation.
 */
export function getRefineResources(): ResourceProps[] {
  const resources: ResourceProps[] = [
    {
      name: "dashboard",
      list: "/",
      meta: {
        label: "Dashboard",
        icon: getIconComponent("HomeOutlined"),
      },
    },
  ];

  // Include ALL modules for resources (hidden ones need routes too)
  for (const module of getAllModules()) {
    resources.push({
      name: module.name,
      list: `/${module.name}`,
      show: `/${module.name}/:id`,
      create: `/${module.name}/create`,
      edit: `/${module.name}/:id/edit`,
      meta: {
        label: module.labelPlural,
        icon: getIconComponent(module.navigation.icon),
        // Mark hidden modules so Refine doesn't show them in auto-generated nav
        hide: module.navigation.hidden,
      },
    });
  }

  return resources;
}

/**
 * Generate React Router Route elements for ALL modules (including hidden).
 * Hidden modules are accessible via direct URL or command palette.
 * Modules without dedicated page components get a placeholder page.
 */
export function getModuleRoutes(): React.ReactNode[] {
  const routes: React.ReactNode[] = [];

  // Include ALL modules for routes (hidden ones are accessible via ⌘K)
  for (const module of getAllModules()) {
    const components = pageComponents[module.name];

    // If no page components exist, render placeholder for all routes
    if (!components) {
      routes.push(
        <Route key={module.name} path={`/${module.name}`}>
          <Route
            index
            element={<PlaceholderPage moduleName={module.labelPlural} />}
          />
          <Route
            path="create"
            element={<PlaceholderPage moduleName={module.labelPlural} />}
          />
          <Route
            path=":id"
            element={<PlaceholderPage moduleName={module.labelPlural} />}
          />
          <Route
            path=":id/edit"
            element={<PlaceholderPage moduleName={module.labelPlural} />}
          />
        </Route>
      );
      continue;
    }

    const { List, Show, Create, Edit } = components;

    routes.push(
      <Route key={module.name} path={`/${module.name}`}>
        <Route
          index
          element={
            <React.Suspense fallback={<div>Loading...</div>}>
              <List />
            </React.Suspense>
          }
        />
        <Route
          path="create"
          element={
            <React.Suspense fallback={<div>Loading...</div>}>
              <Create />
            </React.Suspense>
          }
        />
        <Route
          path=":id/edit"
          element={
            <React.Suspense fallback={<div>Loading...</div>}>
              <Edit />
            </React.Suspense>
          }
        />
        <Route
          path=":id"
          element={
            <React.Suspense fallback={<div>Loading...</div>}>
              <Show />
            </React.Suspense>
          }
        />
      </Route>
    );
  }

  return routes;
}

/**
 * Get navigation items for the context bar.
 * Returns modules sorted by navigation order.
 */
export function getNavigationItems() {
  return getVisibleModules()
    .sort((a, b) => a.navigation.order - b.navigation.order)
    .map((module) => ({
      name: module.name,
      label: module.labelPlural,
      path: `/${module.name}`,
      icon: module.navigation.icon,
      group: module.navigation.group,
    }));
}
